import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { getBoardCellSizeFromArea, getBoardFontSizeFromArea } from '../../utils/gameLayout';

interface NorinoriPuzzle {
  width: number;
  height: number;
  regionMap: number[];
  solution: boolean[];
  seed: number;
  difficulty: number;
}

const difficulties = [
  { orden: 1, nombre: 'Neurona', icono: '🧠' },
  { orden: 2, nombre: 'Sinapsis', icono: '⚡' },
  { orden: 3, nombre: 'Cerebro', icono: '🧩' },
  { orden: 4, nombre: 'Genio', icono: '💡' },
  { orden: 5, nombre: 'Einstein', icono: '🎓' },
];

// null = blanco (sin marcar), true = pintada de negro, false = marcada con X
// (el jugador está seguro de que NO va pintada). Un solo clic/toque va
// rotando por los 3 estados — igual que en Hitori — para que sea usable
// también en tablet/móvil sin depender del clic derecho.
type CellState = null | boolean;

// Cada componente conexo (ortogonal) de celdas pintadas debe tener tamaño
// exactamente 2 — eso equivale a "es un dominó" + "no hay dominós adyacentes".
function findErrorsAndCounts(puzzle: NorinoriPuzzle, cells: CellState[]) {
  const { width, height, regionMap } = puzzle;
  const total = width * height;
  const painted = cells.map((c) => c === true);
  const numRegions = Math.max(...regionMap);
  const regionCount = Array(numRegions + 1).fill(0);
  for (let i = 0; i < total; i++) if (painted[i]) regionCount[regionMap[i]]++;

  const errorCells = new Set<number>();
  for (let i = 0; i < total; i++) {
    if (painted[i] && regionCount[regionMap[i]] > 2) errorCells.add(i);
  }

  const getNeighbors = (index: number): number[] => {
    const row = Math.floor(index / width);
    const col = index % width;
    const res: number[] = [];
    for (const [dr, dc] of [[-1, 0], [0, -1], [0, 1], [1, 0]]) {
      const nr = row + dr, nc = col + dc;
      if (nr >= 0 && nr < height && nc >= 0 && nc < width) res.push(nr * width + nc);
    }
    return res;
  };

  const visited = Array(total).fill(false);
  for (let i = 0; i < total; i++) {
    if (!painted[i] || visited[i]) continue;
    const stack = [i];
    const component: number[] = [];
    visited[i] = true;
    while (stack.length) {
      const cell = stack.pop()!;
      component.push(cell);
      for (const n of getNeighbors(cell)) {
        if (painted[n] && !visited[n]) { visited[n] = true; stack.push(n); }
      }
    }
    if (component.length > 2) component.forEach((c) => errorCells.add(c));
  }

  return { errorCells, regionCount, numRegions };
}

function getRegionBorders(puzzle: NorinoriPuzzle, index: number): string {
  const { width, height, regionMap } = puzzle;
  const row = Math.floor(index / width);
  const col = index % width;
  const regionId = regionMap[index];
  let cls = '';
  if (row === 0 || regionMap[index - width] !== regionId) cls += ' border-t-4';
  if (row === height - 1 || regionMap[index + width] !== regionId) cls += ' border-b-4';
  if (col === 0 || regionMap[index - 1] !== regionId) cls += ' border-l-4';
  if (col === width - 1 || regionMap[index + 1] !== regionId) cls += ' border-r-4';
  return cls;
}

export default function NorinoriGame() {
  const { dificultad } = useParams<{ dificultad: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const dif = parseInt(dificultad || '1');
  const { saveProgress: saveGameProgress } = useGameProgress('norinori', dif);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  const [puzzle, setPuzzle] = useState<NorinoriPuzzle | null>(null);
  const [cells, setCells] = useState<CellState[]>([]);
  const [history, setHistory] = useState<CellState[][]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modoAsistido, setModoAsistido] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moveCount, setMoveCount] = useState(0);
  const [forceNew, setForceNew] = useState(0);
  const [hintCell, setHintCell] = useState<number | null>(null);

  useEffect(() => {
    if (isCompleted) return;
    const interval = setInterval(() => setElapsedTime((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, [isCompleted]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const loadPuzzle = useCallback(async (isNew = false) => {
    setLoading(true);
    setError('');
    try {
      const urlSeed = !isNew ? searchParams.get('seed') : null;
      const seedToUse = urlSeed ? parseInt(urlSeed) : undefined;
      const response = await api.get('/norinori/generate', {
        params: { dificultad: dif, ...(seedToUse && { seed: seedToUse }) },
      });
      const data: NorinoriPuzzle = response.data.data;
      setPuzzle(data);
      const empty: CellState[] = Array(data.width * data.height).fill(null);
      setCells(empty);
      setHistory([[...empty]]);
      setHistIdx(0);
      setMoveCount(0);
      setElapsedTime(0);
      setIsCompleted(false);
      setShowVictory(false);
      setModoAsistido(false);
      setHintCell(null);
      setSearchParams({ seed: data.seed.toString() }, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al generar el puzzle');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dif, forceNew]);

  useEffect(() => { loadPuzzle(forceNew > 0); }, [loadPuzzle]);

  const saveHistoryState = (next: CellState[]) => {
    const snap = [...next];
    const newHist = history.slice(0, histIdx + 1);
    newHist.push(snap);
    if (newHist.length > 200) newHist.shift();
    setHistory(newHist);
    setHistIdx(newHist.length - 1);
  };

  const applyCells = (next: CellState[]) => {
    if (!puzzle) return;
    setCells(next);
    setMoveCount((m) => m + 1);
    saveHistoryState(next);

    const total = puzzle.width * puzzle.height;
    const { errorCells, regionCount, numRegions } = findErrorsAndCounts(puzzle, next);
    let complete = errorCells.size === 0;
    for (let r = 1; r <= numRegions && complete; r++) if (regionCount[r] !== 2) complete = false;
    if (complete) {
      // También debe coincidir exactamente con la solución (única) del puzzle
      // (las marcas con X cuentan como "no pintada", igual que en blanco)
      for (let i = 0; i < total && complete; i++) if ((next[i] === true) !== puzzle.solution[i]) complete = false;
    }
    if (complete) {
      setIsCompleted(true);
      setShowVictory(true);
      saveGameProgress({
        tiempo: elapsedTime,
        movimientos: moveCount + 1,
        // Identifica el tablero jugado: es lo que permite no repetírselo al
        // usuario cuando el nivel se sirve del almacén de pregenerados.
        tablero_id: puzzle.seed,
      });
      if (isInSession) saveSessionResult({ tiempo: elapsedTime, movimientos: moveCount + 1, completado: true });
    }
  };

  // Un solo clic/toque rota: blanco -> negro -> X -> blanco (igual que Hitori),
  // así funciona igual de bien con ratón que con dedo en tablet/móvil.
  const onCellClick = (index: number) => {
    if (!puzzle || isCompleted) return;
    const next = [...cells];
    const current = next[index];
    next[index] = current === null ? true : current === true ? false : null;
    applyCells(next);
  };

  const onUndo = () => {
    if (histIdx <= 0) return;
    setCells([...history[histIdx - 1]]);
    setHistIdx(histIdx - 1);
  };

  const onRedo = () => {
    if (histIdx >= history.length - 1) return;
    setCells([...history[histIdx + 1]]);
    setHistIdx(histIdx + 1);
  };

  const onReset = () => {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    const empty: CellState[] = Array(puzzle.width * puzzle.height).fill(null);
    setCells(empty);
    saveHistoryState(empty);
    setIsCompleted(false);
    setShowVictory(false);
    setHintCell(null);
  };

  const onNewBoard = () => {
    setSearchParams({});
    setForceNew((f) => f + 1);
  };

  const handleHint = async () => {
    if (!puzzle || isCompleted) return;
    try {
      const painted = cells.map((c) => c === true);
      const { data: { data: { hint } } } = await api.post('/norinori/hint', {
        painted, solution: puzzle.solution,
      });
      if (hint) {
        const next = [...cells];
        // Si la solución pinta la celda, la marcamos en negro; si no, la
        // marcamos con X (confirmando que definitivamente va en blanco).
        next[hint.index] = hint.value ? true : false;
        setHintCell(hint.index);
        setTimeout(() => setHintCell(null), 1200);
        applyCells(next);
      }
    } catch { /* ignore */ }
  };

  const handleShowSolution = () => {
    if (!puzzle || !window.confirm('¿Estás seguro? Esto mostrará la solución completa.')) return;
    const solutionCells: CellState[] = puzzle.solution.map((v) => (v ? true : null));
    applyCells(solutionCells);
  };

  const { errorCells, regionCount } = puzzle && modoAsistido
    ? findErrorsAndCounts(puzzle, cells)
    : { errorCells: new Set<number>(), regionCount: [] as number[] };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-white text-2xl">🔄 Generando puzzle…</div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="text-4xl mb-4">⚠️</div>
          <p>{error || 'Error desconocido'}</p>
          <button onClick={onNewBoard} className="mt-4 px-6 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg">
            Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  const cellSize = getBoardCellSizeFromArea(puzzle.width);
  const fontSize = getBoardFontSizeFromArea(puzzle.width, 0.35);

  return (
    <div className="h-full bg-gradient-to-br from-purple-600 to-blue-600 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🁢 Norinori</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {difficulties.map((d) => (
            <button
              key={d.orden}
              onClick={() => { if (dif === d.orden) onNewBoard(); else navigate(`/juegos/norinori/${d.orden}`); }}
              className={`px-2 py-1 rounded-md text-xs font-semibold transition-all ${
                dif === d.orden ? 'bg-white text-purple-700' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {d.icono} <span className="hidden sm:inline">{d.nombre}</span>
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white/70 text-xs hidden sm:inline">⏱ {formatTime(elapsedTime)}</span>
        <div className="flex gap-1 items-center">
          <button onClick={onUndo} disabled={histIdx <= 0} title="Deshacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/></svg>
          </button>
          <button onClick={onRedo} disabled={histIdx >= history.length - 1} title="Rehacer"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 019-9 9 9 0 016 2.3L21 13"/></svg>
          </button>
          <button onClick={() => setModoAsistido((v) => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded-md transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          <button onClick={handleHint} disabled={isCompleted} title="Pista"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18h6M10 22h4M12 2a7 7 0 017 7c0 2.38-1.19 4.47-3 5.74V17H8v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 017-7z"/></svg>
          </button>
          <button onClick={handleShowSolution} disabled={isCompleted} title="Ver solución"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 disabled:opacity-30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          </button>
          <button onClick={onReset} title="Reiniciar"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          </button>
          <button onClick={onNewBoard} title="Nuevo tablero"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M12 8v8M8 12h8"/></svg>
          </button>
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded-md bg-white/20 text-white hover:bg-white/30 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-3 mt-2 p-2 bg-blue-100 border border-blue-300 text-blue-800 rounded-lg text-sm text-center">
          {error}
        </div>
      )}

      <div className="flex-1 min-h-0 flex items-center justify-center p-2" style={{ containerType: 'size' }}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: `repeat(${puzzle.width}, ${cellSize})`,
            gridTemplateRows: `repeat(${puzzle.height}, ${cellSize})`,
          }}
        >
          {Array.from({ length: puzzle.width * puzzle.height }, (_, i) => {
            const isPainted = cells[i] === true;
            const isMarkedX = cells[i] === false;
            const hasError = errorCells.has(i);
            const isHint = hintCell === i;
            const rid = puzzle.regionMap[i];
            const badgeCount = regionCount[rid];

            let bg = '#ffffff';
            if (isCompleted) bg = isPainted ? '#111827' : '#dcfce7';
            else if (hasError) bg = isPainted ? '#991b1b' : '#fecaca';
            else if (isPainted) bg = '#111827';
            else if (isHint) bg = '#fde68a';

            return (
              <button
                key={i}
                onClick={() => onCellClick(i)}
                disabled={isCompleted}
                className={`relative flex items-center justify-center transition-all border-slate-300 ${getRegionBorders(puzzle, i)}`}
                style={{ width: cellSize, height: cellSize, backgroundColor: bg, borderColor: '#1e293b' }}
              >
                {isMarkedX && (
                  <span className="select-none font-bold" style={{ fontSize: `calc(${fontSize} * 1.3)`, color: '#94a3b8' }}>
                    ✕
                  </span>
                )}
                {modoAsistido && cells[i] === null && badgeCount !== undefined && badgeCount < 2 && (
                  <span
                    className="select-none font-semibold"
                    style={{ fontSize, color: hasError ? '#991b1b' : '#94a3b8' }}
                  >
                    {badgeCount}/2
                  </span>
                )}
                {modoAsistido && !hasError && badgeCount === 2 && (
                  <span
                    className="absolute select-none"
                    style={{ top: 2, right: 2, fontSize: `calc(${fontSize} * 0.7)`, color: isPainted ? '#4ade80' : '#16a34a' }}
                  >
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <VictoryModal
        isOpen={showVictory}
        onClose={() => setShowVictory(false)}
        stats={{ tiempo: elapsedTime, movimientos: moveCount }}
      />

      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Norinori"
        gameIcon="🁢"
        objective="Pinta casillas de negro de forma que cada región tenga exactamente 2 casillas pintadas, y que esas casillas formen siempre parejas de dominó (2x1 u 1x2)."
        rules={[
          'El tablero está dividido en regiones irregulares.',
          'Cada región debe tener exactamente 2 casillas pintadas.',
          'Cada casilla pintada debe formar parte de un dominó (2 casillas pintadas adyacentes en horizontal o vertical). Un dominó puede cruzar los bordes de dos regiones.',
          'Dos dominós no pueden tocarse en horizontal o vertical (solo en diagonal).',
        ]}
        howToPlay="Toca o haz clic en una casilla para ir rotando su estado: blanco → negro → marcada con ✕ (para anotar que seguro que NO va pintada) → blanco de nuevo. El escudo activa el modo asistido: resalta en rojo las casillas que violan alguna regla y muestra en cada región cuántas casillas lleva pintadas (X/2) hasta completarla."
        author="Nikoli (Japón)"
      />
    </div>
  );
}
